import { onSnapshot } from "firebase/firestore";
import { auth } from "../firebaseConfig";

// Erros transitórios que valem retry. No mobile, `permission-denied` num
// listener quase sempre significa auth == null no instante da avaliação:
// corrida de token no boot ou token expirado ao retomar o app do segundo
// plano. `unavailable`/`unauthenticated` são rede/sessão momentaneamente fora.
const RETRIABLE = new Set([
  "permission-denied",
  "unavailable",
  "unauthenticated",
]);
const DEFAULT_MAX_RETRIES = 5;

/**
 * Wrapper de onSnapshot com tratamento de erro + retry automático.
 *
 * Drop-in para `onSnapshot(ref, onNext)`: retorna uma função de unsubscribe.
 * Ao receber um erro transitório, renova o token de auth e reanexa o listener
 * com backoff exponencial — em vez de morrer calado (o que deixava a tela
 * presa no "loading" e imprimia "Uncaught Error in snapshot listener").
 *
 * @param {*} reference Query | CollectionReference | DocumentReference
 * @param {(snap:any)=>void} onNext callback de sucesso
 * @param {{label?:string, onError?:(e:any)=>void, maxRetries?:number}} [options]
 * @returns {() => void} unsubscribe (cancela retries pendentes também)
 */
export function safeOnSnapshot(reference, onNext, options = {}) {
  const {
    label = "listener",
    onError,
    maxRetries = DEFAULT_MAX_RETRIES,
  } = options;

  let cancelled = false;
  let unsub = null;
  let retryTimer = null;
  let retries = 0;

  const clearRetry = () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const attach = () => {
    if (cancelled) return;
    unsub = onSnapshot(
      reference,
      (snap) => {
        retries = 0; // sucesso zera o backoff
        onNext(snap);
      },
      async (error) => {
        console.error(
          `[safeOnSnapshot:${label}] ${error.code || "erro"}:`,
          error.message
        );
        if (onError) {
          try {
            onError(error);
          } catch (_) {
            /* callback do chamador não deve derrubar o retry */
          }
        }

        // Sem usuário logado (ex.: logout) reanexar não adianta.
        if (!auth.currentUser) return;
        if (cancelled || !RETRIABLE.has(error.code) || retries >= maxRetries) {
          return;
        }

        retries += 1;

        // Renova o token ANTES de reanexar — cobre o permission-denied causado
        // por token atrasado/expirado (boot lento ou app retomado no celular).
        try {
          await auth.currentUser.getIdToken(true);
        } catch (_) {
          /* segue para o retry mesmo assim */
        }
        if (cancelled) return;

        // Backoff exponencial: 1s, 2s, 4s, 8s… com teto de 15s.
        const delay = Math.min(1000 * 2 ** (retries - 1), 15000);
        clearRetry();
        retryTimer = setTimeout(() => {
          if (cancelled) return;
          if (unsub) {
            try {
              unsub();
            } catch (_) {
              /* noop */
            }
          }
          attach();
        }, delay);
      }
    );
  };

  attach();

  return () => {
    cancelled = true;
    clearRetry();
    if (unsub) {
      try {
        unsub();
      } catch (_) {
        /* noop */
      }
    }
  };
}
